import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { SyutSelect } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { formatBackendTimestamps } from 'utils/errorHandling';
import { guestService } from 'api/services/guestService';
import type { Guest, CreateGuestRequest, UpdateGuestRequest } from 'api/types';
import type { SelectOption } from 'components/ui/SyutSelect';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface GuestModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  record?: Guest | null;
  onSave: () => void;
  onClose: () => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onReopen?: () => void;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, guestId?: string) => void;
}

interface GuestFormData {
  title: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  nationality: string;
  dateOfBirth: string;
  address: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  idType: string;
  idNumber: string;
  isVip: boolean;
  notes: string;
  isActive: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const INITIAL_FORM_DATA: GuestFormData = {
  title: '',
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  nationality: '',
  dateOfBirth: '',
  address: '',
  city: '',
  state: '',
  country: '',
  pincode: '',
  idType: '',
  idNumber: '',
  isVip: false,
  notes: '',
  isActive: true,
};

const TITLE_OPTIONS: SelectOption[] = [
  { value: 'Mr', label: 'Mr' },
  { value: 'Mrs', label: 'Mrs' },
  { value: 'Ms', label: 'Ms' },
  { value: 'Dr', label: 'Dr' },
  { value: 'Prof', label: 'Prof' },
];

const ID_TYPE_OPTIONS: SelectOption[] = [
  { value: 'Aadhar', label: 'Aadhar' },
  { value: 'Passport', label: 'Passport' },
  { value: 'DL', label: 'Driving License' },
  { value: 'Voter ID', label: 'Voter ID' },
  { value: 'PAN', label: 'PAN' },
  { value: 'Govt ID', label: 'Government ID' },
];

// ============================================================================
// COMPONENT
// ============================================================================

const GuestModal: React.FC<GuestModalProps> = ({
  isOpen, mode, record, onSave, onClose,
  tabId, moduleName, onReopen, onRestoreModal, createInstanceId, copyInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();
  // --- Internal form state (Pattern B) ---
  const [formData, setFormData] = useState<GuestFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const recordRef = useRef(record);
  recordRef.current = record;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId);
  copyInstanceIdRef.current = copyInstanceId;
  const isRestoreRef = useRef(false);
  const initialSnapshotRef = useRef('');

  // --- Validation state ---
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string): string | undefined => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // --- Confirm dialog state ---
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // --- "More Details" collapsible (DOB, Nationality, Address, Notes). Auto-expands
  //     on edit when any of these fields already carry data. None are required. ---
  const [moreOpen, setMoreOpen] = useState(false);

  // --- Concurrency conflict state ---
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const effectiveRecordRef = useRef<Guest | null | undefined>(record);

  // --- Populate form on open ---
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    effectiveRecordRef.current = record;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const rec = recordRef.current;
    let data: GuestFormData;
    if ((mode === 'edit' || mode === 'copy') && rec) {
      data = {
        title: rec.title || '',
        firstName: rec.firstName || '',
        lastName: rec.lastName || '',
        phone: rec.phone || '',
        email: mode === 'copy' ? '' : (rec.email || ''),
        nationality: rec.nationality || '',
        dateOfBirth: rec.dateOfBirth ? rec.dateOfBirth.split('T')[0] : '',
        address: rec.address || '',
        city: rec.city || '',
        state: rec.state || '',
        country: rec.country || '',
        pincode: rec.pincode || '',
        idType: rec.idType || '',
        idNumber: rec.idNumber || '',
        isVip: rec.isVip ?? false,
        notes: rec.notes || '',
        isActive: rec.isActive,
      };
    } else {
      data = { ...INITIAL_FORM_DATA };
    }
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
    setMoreOpen(!!(data.dateOfBirth || data.nationality || data.address || data.city ||
      data.state || data.country || data.pincode || data.notes));
  }, [isOpen]);

  // --- Unsaved changes tracking ---
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  // --- Close handler (checks dirty state) ---
  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  // --- Minimize handler ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentCopyInstanceId = copyInstanceIdRef.current;
    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else if (currentMode === 'copy') {
      modalId = `${tabId}-form-modal-copy-${currentCopyInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${recordRef.current?.id || 'new'}`;
    }
    let title: string;
    if (currentMode === 'create') {
      const name = currentFormData.firstName?.trim();
      const lastName = currentFormData.lastName?.trim();
      const displayName = name ? `${name}${lastName ? ' ' + lastName : ''}` : '';
      const idx = currentCreateInstanceId || '1';
      title = displayName ? `Add - ${displayName} (${idx})` : `Add New (${idx})`;
    } else if (currentMode === 'copy') {
      const name = currentFormData.firstName?.trim();
      const lastName = currentFormData.lastName?.trim();
      const displayName = name ? `${name}${lastName ? ' ' + lastName : ''}` : '';
      const idx = currentCopyInstanceId || '1';
      title = displayName ? `Copy - ${displayName} (${idx})` : `Copy (${idx})`;
    } else {
      const name = currentFormData.firstName?.trim();
      const lastName = currentFormData.lastName?.trim();
      const displayName = name ? `${name}${lastName ? ' ' + lastName : ''}` : 'Guest';
      title = `Edit - ${displayName}`;
    }
    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;
    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) onRestoreModal(
          currentMode,
          currentMode === 'create' ? currentCreateInstanceId : currentMode === 'copy' ? currentCopyInstanceId : undefined,
          currentMode !== 'create' ? recordRef.current?.id : undefined,
        );
        else if (onReopen) onReopen();
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, onReopen]);

  // Expose handleMinimize to parent for auto-minimize on new copy
  if (minimizeRef) minimizeRef.current = handleMinimize;

  // --- Validate + show save confirmation ---
  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First Name is required';
    } else if (formData.firstName.length > 50) {
      errors.firstName = 'First Name cannot exceed 50 characters';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last Name is required';
    } else if (formData.lastName.length > 50) {
      errors.lastName = 'Last Name cannot exceed 50 characters';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (formData.title && formData.title.length > 10) {
      errors.title = 'Title cannot exceed 10 characters';
    }

    if (formData.phone && formData.phone.length > 20) {
      errors.phone = 'Phone cannot exceed 20 characters';
    }

    // Email uniqueness validation (async) — only if email provided
    if (!errors.email && formData.email.trim()) {
      try {
        const excludeId = mode === 'edit' ? record?.id : undefined;
        const res = await guestService.validateEmail(formData.email.trim(), excludeId);
        if (res.data === false) {
          errors.email = 'A guest with this email already exists';
        }
      } catch {
        // Skip validation on network error
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLElement>('input')?.focus();
        }
      });
      return;
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, mode, record]);

  // --- Confirmed save ---
  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);

    try {
      const payload: CreateGuestRequest | UpdateGuestRequest = {
        title: formData.title || undefined,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim() || undefined,
        email: formData.email.trim() || undefined,
        nationality: formData.nationality || undefined,
        dateOfBirth: formData.dateOfBirth || undefined,
        address: formData.address.trim() || undefined,
        city: formData.city.trim() || undefined,
        state: formData.state.trim() || undefined,
        country: formData.country.trim() || undefined,
        pincode: formData.pincode.trim() || undefined,
        idType: formData.idType || undefined,
        idNumber: formData.idNumber.trim() || undefined,
        isVip: formData.isVip,
        notes: formData.notes.trim() || undefined,
        isActive: formData.isActive,
      };

      if (mode === 'edit') {
        const eff = effectiveRecordRef.current!;
        await guestService.update(eff.id, { ...payload, rowVersion: eff.rowVersion } as UpdateGuestRequest);
      } else {
        await guestService.create(payload as CreateGuestRequest);
      }

      setIsSaving(false);
      const displayName = `${formData.firstName} ${formData.lastName}`.trim();
      notificationService.toast.success(
        `"${displayName}" ${mode === 'edit' ? 'updated' : 'created'} successfully.`
      );
      onSave();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message;
      if (status === 409 && backendMessage) {
        setConcurrencyConflict({ open: true, message: formatBackendTimestamps(backendMessage) });
      } else {
        notificationService.toast.error(backendMessage || 'An error occurred. Please try again.');
      }
    }
  }, [formData, mode, onSave]);

  // --- Refresh record from server after concurrency conflict ---
  const handleRefreshFromConflict = useCallback(async () => {
    const eff = effectiveRecordRef.current;
    if (!eff) {
      setConcurrencyConflict({ open: false, message: '' });
      return;
    }
    setIsRefreshing(true);
    try {
      const response = await guestService.getById(eff.id);
      const rec = response.data;
      if (rec) {
        const data: GuestFormData = {
          title: rec.title || '',
          firstName: rec.firstName || '',
          lastName: rec.lastName || '',
          phone: rec.phone || '',
          email: rec.email || '',
          nationality: rec.nationality || '',
          dateOfBirth: rec.dateOfBirth ? rec.dateOfBirth.split('T')[0] : '',
          address: rec.address || '',
          city: rec.city || '',
          state: rec.state || '',
          country: rec.country || '',
          pincode: rec.pincode || '',
          idType: rec.idType || '',
          idNumber: rec.idNumber || '',
          isVip: rec.isVip ?? false,
          notes: rec.notes || '',
          isActive: rec.isActive,
        };
        setFormData(data);
        initialSnapshotRef.current = JSON.stringify(data);
        setMoreOpen(!!(data.dateOfBirth || data.nationality || data.address || data.city ||
          data.state || data.country || data.pincode || data.notes));
        effectiveRecordRef.current = rec;
        setFieldErrors({});
        notificationService.toast.success('Loaded the latest version. Please review and save.');
      }
      setConcurrencyConflict({ open: false, message: '' });
    } catch (err: any) {
      const backendMessage =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0] ||
        err?.message ||
        'Failed to load the latest version.';
      notificationService.toast.error(backendMessage);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  if (!isOpen) return null;

  const displayName = `${formData.firstName} ${formData.lastName}`.trim() || 'Guest';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        {/* Loading overlay */}
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem'
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite'
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {mode === 'edit' ? 'Saving...' : 'Creating...'}
            </span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header">
          <h2 className="modal-title">{mode === 'create' ? 'Add Guest' : mode === 'copy' ? 'Copy Guest' : 'Edit Guest'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* --- Basic Information --- */}
          <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 1fr 1fr', gap: '1rem' }}>
            <div className="form-group" data-field="title">
              <SyutSelect
                label="Title"
                options={TITLE_OPTIONS}
                value={formData.title}
                onChange={(val) => setFormData(prev => ({ ...prev, title: (val as string) || '' }))}
                placeholder="Select..."
                isClearable
                size="md"
              />
            </div>

            <div className="form-group" data-field="firstName">
              <label className="form-label">First Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('firstName') ? ' form-field-error' : ''}`}
                value={formData.firstName}
                onChange={(e) => { clearFieldError('firstName'); setFormData(prev => ({ ...prev, firstName: e.target.value })); }}
                placeholder="e.g., John"
                maxLength={50}
                autoFocus
              />
              {getFieldError('firstName') && <div className="form-error-message">{getFieldError('firstName')}</div>}
            </div>

            <div className="form-group" data-field="lastName">
              <label className="form-label">Last Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('lastName') ? ' form-field-error' : ''}`}
                value={formData.lastName}
                onChange={(e) => { clearFieldError('lastName'); setFormData(prev => ({ ...prev, lastName: e.target.value })); }}
                placeholder="e.g., Doe"
                maxLength={50}
              />
              {getFieldError('lastName') && <div className="form-error-message">{getFieldError('lastName')}</div>}
            </div>
          </div>

          {/* --- Status + VIP (Nationality moved to More Details) --- */}
          <div className="form-grid-2">
            <div className="form-group" data-field="isActive">
              <SyutSelect
                label="Status"
                options={[
                  { value: 'true', label: 'Active' },
                  { value: 'false', label: 'Inactive' },
                ]}
                value={String(formData.isActive)}
                onChange={(val) => setFormData(prev => ({ ...prev, isActive: val === 'true' }))}
                isClearable={false}
                size="md"
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', minHeight: '2.25rem', marginTop: '1.5rem' }}>
                <input
                  type="checkbox"
                  checked={formData.isVip}
                  onChange={(e) => setFormData(prev => ({ ...prev, isVip: e.target.checked }))}
                  style={{ width: '1rem', height: '1rem', accentColor: 'var(--primary)' }}
                />
                <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>VIP Guest</span>
              </label>
            </div>
          </div>

          {/* --- Contact Information (Phone + Email only — DOB moved to More Details) --- */}
          <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>Contact Information</h3>
            <div className="form-grid-2">
              <div className="form-group" data-field="phone">
                <label className="form-label">Phone</label>
                <input type="text"
                  className={`form-input${getFieldError('phone') ? ' form-field-error' : ''}`}
                  value={formData.phone}
                  onChange={(e) => { clearFieldError('phone'); setFormData(prev => ({ ...prev, phone: e.target.value })); }}
                  placeholder="e.g., +91 9876543210" maxLength={20} />
                {getFieldError('phone') && <div className="form-error-message">{getFieldError('phone')}</div>}
              </div>

              <div className="form-group" data-field="email">
                <label className="form-label">Email</label>
                <input type="email"
                  className={`form-input${getFieldError('email') ? ' form-field-error' : ''}`}
                  value={formData.email}
                  onChange={(e) => { clearFieldError('email'); setFormData(prev => ({ ...prev, email: e.target.value })); }}
                  placeholder="e.g., john.doe@example.com" maxLength={255} />
                {getFieldError('email') && <div className="form-error-message">{getFieldError('email')}</div>}
              </div>
            </div>
          </div>

          {/* --- Identity Document (kept visible — cashiers fill these at booking time) --- */}
          <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>Identity Document</h3>
            <div className="form-grid-2">
              <div className="form-group" data-field="idType">
                <SyutSelect
                  label="ID Type"
                  options={ID_TYPE_OPTIONS}
                  value={formData.idType}
                  onChange={(val) => setFormData(prev => ({ ...prev, idType: (val as string) || '' }))}
                  placeholder="Select ID type..."
                  isClearable
                  size="md"
                />
              </div>

              <div className="form-group" data-field="idNumber">
                <label className="form-label">ID Number</label>
                <input type="text" className="form-input" value={formData.idNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, idNumber: e.target.value }))}
                  placeholder="e.g., XXXX-XXXX-XXXX" maxLength={50} />
              </div>
            </div>
          </div>

          {/* --- More Details ▾ — collapsible, all fields optional --- */}
          <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.75rem' }}>
            <button
              type="button"
              onClick={() => setMoreOpen(o => !o)}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)',
                display: 'flex', alignItems: 'center', gap: '0.375rem', padding: 0,
              }}
            >
              <IconSystem.chevronRight size={14} style={{ transform: moreOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
              More Details
              <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', fontWeight: 400 }}>(DOB, nationality, address, notes — optional)</span>
            </button>

            {moreOpen && (
              <div style={{ marginTop: '0.75rem' }}>
                <div className="form-grid-2">
                  <div className="form-group" data-field="dateOfBirth">
                    <label className="form-label">Date of Birth</label>
                    <input type="date" className="form-input" value={formData.dateOfBirth}
                      onChange={(e) => setFormData(prev => ({ ...prev, dateOfBirth: e.target.value }))} />
                  </div>

                  <div className="form-group" data-field="nationality">
                    <label className="form-label">Nationality</label>
                    <input type="text" className="form-input" value={formData.nationality}
                      onChange={(e) => setFormData(prev => ({ ...prev, nationality: e.target.value }))}
                      placeholder="e.g., Indian" maxLength={50} />
                  </div>
                </div>

                <div className="form-group" data-field="address" style={{ marginTop: '0.5rem' }}>
                  <label className="form-label">Address</label>
                  <input type="text" className="form-input" value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="e.g., 123 Main Street, Apt 4B" />
                </div>

                <div className="form-grid-4" style={{ marginTop: '0.5rem' }}>
                  <div className="form-group" data-field="city">
                    <label className="form-label">City</label>
                    <input type="text" className="form-input" value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="e.g., Mumbai" maxLength={100} />
                  </div>

                  <div className="form-group" data-field="state">
                    <label className="form-label">State</label>
                    <input type="text" className="form-input" value={formData.state}
                      onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                      placeholder="e.g., Maharashtra" maxLength={100} />
                  </div>

                  <div className="form-group" data-field="country">
                    <label className="form-label">Country</label>
                    <input type="text" className="form-input" value={formData.country}
                      onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                      placeholder="e.g., India" maxLength={100} />
                  </div>

                  <div className="form-group" data-field="pincode">
                    <label className="form-label">Pincode</label>
                    <input type="text" className="form-input" value={formData.pincode}
                      onChange={(e) => setFormData(prev => ({ ...prev, pincode: e.target.value }))}
                      placeholder="e.g., 400001" maxLength={10} />
                  </div>
                </div>

                <div className="form-group" data-field="notes" style={{ marginTop: '0.5rem' }}>
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-input"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional notes about the guest..."
                    rows={3}
                    style={{ resize: 'vertical', minHeight: '4rem' }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving || (mode === 'edit' && !isDirty)}
            title={mode === 'edit' && !isDirty ? 'No changes to save' : undefined}
          >
            <IconSystem.save size={16} />
            {mode === 'edit' ? 'Save Changes' : 'Create Guest'}
          </button>
        </div>
      </div>

      {/* Save confirmation */}
      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={`Are you sure you want to ${mode === 'edit' ? 'update' : 'create'} the guest "${displayName}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      {/* Discard changes confirmation */}
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      {/* Concurrency conflict — refresh from server */}
      <ConfirmDialog
        open={concurrencyConflict.open}
        title="Record Modified by Another User"
        message={concurrencyConflict.message}
        confirmLabel="Load Latest"
        cancelLabel="Keep My Changes"
        variant="default"
        loading={isRefreshing}
        loadingMessage="Loading latest version..."
        onConfirm={handleRefreshFromConflict}
        onCancel={() => setConcurrencyConflict({ open: false, message: '' })}
      />
    </>
  );
};

export default GuestModal;
